/**
 * Hook that hydrates voice-feature state for the active voice channel:
 *   - host status (drives REC button + lock toggle visibility)
 *   - existing recordings (so the REC indicator is correct on join)
 *   - subscribes to recording / waiting / lock socket events
 *
 * One-shot per channelId — designed to be called from a top-level voice
 * UI container (Chat.tsx) so the data is ready when the user lands.
 */
import { useEffect } from 'react'
import { channelApi } from '../services/api'
import { useRecordingStore } from '@os/spaces/web/stores/recordingStore'
import { useWaitingRoomStore } from '@os/spaces/web/stores/waitingRoomStore'
import { useVoiceStore } from '../stores/voiceStore'
import { useLivekitStore } from '../stores/livekitStore'
import { useAuthStore } from '../stores/authStore'
import { getSocket } from '../services/socket'

export function useVoiceFeatures(channelId: string | null | undefined) {
  const loadList = useRecordingStore((s) => s.loadList)
  const loadHostStatus = useRecordingStore((s) => s.loadHostStatus)
  const onStarted = useRecordingStore((s) => s.onStarted)
  const onStopped = useRecordingStore((s) => s.onStopped)
  const onUpdated = useRecordingStore((s) => s.onUpdated)

  const setSelfWaiting = useWaitingRoomStore((s) => s.setSelfWaiting)
  const setSelfApproved = useWaitingRoomStore((s) => s.setSelfApproved)
  const setSelfRejected = useWaitingRoomStore((s) => s.setSelfRejected)
  const addRequest = useWaitingRoomStore((s) => s.addRequest)
  const resolveRequest = useWaitingRoomStore((s) => s.resolveRequest)

  useEffect(() => {
    if (!channelId) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await channelApi.hostStatus(channelId)
        if (!cancelled) loadHostStatus(channelId, !!res.data?.isHost)
      } catch {}
      loadList(channelId)
    })()
    return () => { cancelled = true }
  }, [channelId, loadHostStatus, loadList])

  useEffect(() => {
    const socket = getSocket()

    const onRecStarted = (data: any) => {
      onStarted({
        id: data.recordingId,
        channelId: data.channelId,
        startedById: data.startedById,
        status: 'ACTIVE',
        startedAt: data.startedAt,
        endedAt: null,
        duration: 0,
        fileUrl: null,
        fileSize: null,
        format: 'mp4',
      })
    }
    const onRecStopped = (data: any) => onStopped(data.channelId, data.recordingId)
    const onRecUpdated = (data: any) =>
      onUpdated(data.channelId, {
        recordingId: data.recordingId,
        status: data.status,
        fileUrl: data.fileUrl,
        duration: data.duration,
        fileSize: data.fileSize,
      })

    const onWaiting = (data: any) => setSelfWaiting(data.channelId, data.waitingId)
    const onApproved = (data: any) => {
      setSelfApproved(data.channelId)
      // Retry the LiveKit connect now that the host let us in. We only
      // retry if the user is still on this channel — otherwise they
      // already moved on.
      const cur = useVoiceStore.getState().currentChannelId
      if (cur === data.channelId) {
        const u = useAuthStore.getState().user
        const username = u ? (u.username || u.firstName || 'User') : 'User'
        // OSPROD-267: this path used to bring up MEDIA ONLY. Every other join
        // path emits `voice:join`, which is the sole thing that puts the
        // socket into `voice:<id>` — the room the server scopes transcript
        // broadcasts AND the history backfill to. Approved-from-lobby users
        // therefore saw and heard everyone while their transcript panel
        // stayed empty forever (and had no VoiceState row / presence either).
        // Mirror VoiceChannelView's join, including the device prefs this
        // call was silently dropping.
        const { audioInputDeviceId, isNoiseSuppressionOn } = useVoiceStore.getState()
        useLivekitStore.getState()
          .connect(data.channelId, username, audioInputDeviceId, isNoiseSuppressionOn)
          .then(() => {
            // room === null means the sticky-ownership gate refused (voice is
            // live on another device) — registering presence would be a lie.
            if (!useLivekitStore.getState().room) return
            getSocket().emit('voice:join', { channelId: data.channelId, userId: u?.id })
            getSocket().emit('voice:getParticipants', { channelId: data.channelId })
          })
          .catch(() => {})
      }
    }
    const onRejected = (data: any) => setSelfRejected(data.channelId)
    const onRequest = (data: any) => addRequest({
      waitingId: data.waitingId,
      channelId: data.channelId,
      userId: data.userId,
      username: data.username,
      avatarUrl: data.avatarUrl,
      requestedAt: data.requestedAt,
    })
    const onResolved = (data: any) => resolveRequest(data.channelId, data.waitingId)

    socket.on('voice:recording:started', onRecStarted)
    socket.on('voice:recording:stopped', onRecStopped)
    socket.on('voice:recording:updated', onRecUpdated)
    socket.on('voice:waiting:waiting', onWaiting)
    socket.on('voice:waiting:approved', onApproved)
    socket.on('voice:waiting:rejected', onRejected)
    socket.on('voice:waiting:request', onRequest)
    socket.on('voice:waiting:resolved', onResolved)

    return () => {
      socket.off('voice:recording:started', onRecStarted)
      socket.off('voice:recording:stopped', onRecStopped)
      socket.off('voice:recording:updated', onRecUpdated)
      socket.off('voice:waiting:waiting', onWaiting)
      socket.off('voice:waiting:approved', onApproved)
      socket.off('voice:waiting:rejected', onRejected)
      socket.off('voice:waiting:request', onRequest)
      socket.off('voice:waiting:resolved', onResolved)
    }
  }, [onStarted, onStopped, onUpdated, setSelfWaiting, setSelfApproved, setSelfRejected, addRequest, resolveRequest])
}
